import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { termsService } from '@/services/auth/termsService.js';

const TermsViewer = ({ fileUrl }) => {
  const { t } = useTranslation();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileType, setFileType] = useState(null);

  const cleanHtml = (html) => {  
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    const bodyContent = bodyMatch ? bodyMatch[1] : html;
  
    const cleaned = bodyContent
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
      .replace(/<meta[^>]*>/gi, '')
      .replace(/<link[^>]*>/gi, '')
      .replace(/<title[^>]*>.*?<\/title>/gi, '')
      .replace(/<\!DOCTYPE[^>]*>/gi, '');
  
    return cleaned;
  };

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get file extension
        const extension = fileUrl.split('.').pop().toLowerCase();
        setFileType(extension);

        if (extension === 'pdf') {
          setContent({ type: 'pdf', url: fileUrl });
        } else if (extension === 'html' || extension === 'txt') {
          const text = await termsService.getTermContent(fileUrl);
          if (extension === 'html') {
            setContent({ type: 'text', content: cleanHtml(text) });
          } else {
            setContent({ type: 'text', content: text });
          }
        } else {
          setContent({ type: 'download', url: fileUrl });
        }
      } catch (err) {
        console.error('Error loading terms:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (fileUrl) {
      loadContent();
    }
  }, [fileUrl]);

  if (loading) {
    return <div className="flex justify-center items-center p-4">{t('common.loading')}</div>;
  }

  if (error) {
    return (
      <div className="text-red-500 p-4">
        {t('common.error')}: {error}
      </div>
    );
  }

  if (!content) {
    return null;
  }

  switch (content.type) {
    case 'pdf':
      return (
        <div className="w-full h-full">
          <iframe
            src={`https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(content.url)}`}
            className="w-full h-full min-h-[500px]"
            title="PDF Viewer"
          />
        </div>
      );

    case 'text':
      return (
        <div className="p-4 overflow-auto">
          <div 
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: content.content }}
          />
        </div>
      );

    case 'download':
      return (
        <div className="p-4 text-center">
          <a
            href={content.url}
            download
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {t('terms.download')} {fileType.toUpperCase()}
          </a>
        </div>
      );

    default:
      return null;
  }
};

TermsViewer.propTypes = {
  fileUrl: PropTypes.string.isRequired,
};

export default TermsViewer; 
